import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { NapakaApi, sporocilo } from '../api/napaka';
import { api, podatki, type Shema } from '../api/odjemalec';
import { denar, odstotek } from '../oblika/oblika';
import { StanjeNalaganja } from '../postavitev/Podatki';
import { usePravica } from '../seja/pravice';
import { GlavaZaslona, ZaslonSeznama } from '../seznam/Seznam';
import { obrezano } from './delavnica';
import { useCenikHotelaGum } from './gume';

type Postavka = Shema['PostavkaHotelaGum'];

/**
 * Cenik hotela za gume: storitve hrambe (cena brez DDV, enota, DDV – prazno = stopnja podjetja), ki jih ob prevzemu gum
 * izberete za račun. Dodajanje in urejanje v oknu, »Odstrani« postavko skrije (prevzeti kompleti obdržijo kopijo).
 */
export function CenikHotelaGum() {
  const ureja = usePravica('DELAVNICA', 'UREJANJE');
  const [urejana, setUrejana] = useState<Postavka | null>(null);
  const odjemalec = useQueryClient();
  const cenik = useCenikHotelaGum();
  const odstrani = useMutation({
    mutationFn: (id: number) => podatki(api.DELETE('/api/delavnica/gume/cenik/{id}', { params: { path: { id } } })),
    onSuccess: async () => {
      notifications.show({ color: 'green', message: 'Postavka je odstranjena iz cenika.' });
      await odjemalec.invalidateQueries({ queryKey: ['cenik-hotela-gum'] });
    },
  });

  return (
    <ZaslonSeznama>
      <Button
        component={Link}
        to="/delavnica/gume"
        variant="subtle"
        leftSection={<IconArrowLeft size={16} />}
        mb="xs"
        px={0}
      >
        Hotel za gume
      </Button>
      <GlavaZaslona
        naslov="Cenik hotela za gume"
        opis="Storitve hrambe, ki jih ob prevzemu gum izberete za račun."
        pomoc="hotel-za-gume"
        dejanja={
          ureja && (
            <Button leftSection={<IconPlus size={18} />} onClick={() => setUrejana({ enota: 'kpl', aktivno: true })}>
              Nova postavka
            </Button>
          )
        }
      />
      <Paper radius="lg" p="md" shadow="xs">
        <Stack gap="sm">
          {cenik.isError ? (
            <StanjeNalaganja
              napaka={cenik.error}
              ponovi={() => void cenik.refetch()}
              naslov="Cenika ni bilo mogoče naložiti"
            />
          ) : (cenik.data ?? []).length === 0 ? (
            <Text size="sm" c="dimmed" py="md">
              {cenik.isPending ? 'Nalagam …' : 'V ceniku še ni postavk. Dodajte npr. »Hramba kompleta gum – sezona«.'}
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={520}>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Naziv</Table.Th>
                    <Table.Th>Enota</Table.Th>
                    <Table.Th ta="right">Cena brez DDV</Table.Th>
                    <Table.Th ta="right">DDV</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(cenik.data ?? []).map((p) => (
                    <Table.Tr key={p.id}>
                      <Table.Td fw={600}>{p.naziv}</Table.Td>
                      <Table.Td>{p.enota}</Table.Td>
                      <Table.Td ta="right" className="brez-preloma">
                        {denar(p.cena)}
                      </Table.Td>
                      <Table.Td ta="right" className="brez-preloma">
                        {p.ddvStopnja != null ? `${odstotek(p.ddvStopnja)} %` : 'podjetja'}
                      </Table.Td>
                      <Table.Td ta="right" className="brez-preloma">
                        {ureja && (
                          <>
                            <ActionIcon variant="subtle" aria-label={`Uredi ${p.naziv}`} onClick={() => setUrejana(p)}>
                              <IconPencil size={18} />
                            </ActionIcon>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              aria-label={`Odstrani ${p.naziv}`}
                              onClick={() => p.id && odstrani.mutate(p.id)}
                              loading={odstrani.isPending && odstrani.variables === p.id}
                            >
                              <IconTrash size={18} />
                            </ActionIcon>
                          </>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
          {odstrani.error && (
            <Alert color="red" variant="light" role="alert">
              {sporocilo(odstrani.error)}
            </Alert>
          )}
        </Stack>
      </Paper>
      <Modal
        opened={!!urejana}
        onClose={() => setUrejana(null)}
        title={urejana?.id ? 'Uredi postavko cenika' : 'Nova postavka cenika'}
      >
        {urejana && <ObrazecPostavke key={urejana.id ?? 'nova'} zacetek={urejana} zapri={() => setUrejana(null)} />}
      </Modal>
    </ZaslonSeznama>
  );
}

function ObrazecPostavke({ zacetek, zapri }: { zacetek: Postavka; zapri: () => void }) {
  const odjemalec = useQueryClient();
  const [p, setP] = useState(zacetek);
  const [polja, setPolja] = useState<Record<string, string>>({});
  const shrani = useMutation({
    mutationFn: () => {
      const body = { ...p, naziv: obrezano(p.naziv), enota: obrezano(p.enota) };
      return p.id
        ? podatki(api.PUT('/api/delavnica/gume/cenik/{id}', { params: { path: { id: p.id } }, body }))
        : podatki(api.POST('/api/delavnica/gume/cenik', { body }));
    },
    onMutate: () => setPolja({}),
    onSuccess: async () => {
      await odjemalec.invalidateQueries({ queryKey: ['cenik-hotela-gum'] });
      zapri();
    },
    onError: (e) => e instanceof NapakaApi && setPolja(e.polja),
  });
  const decimalno = { decimalSeparator: ',', allowNegative: false, decimalScale: 2 } as const;
  return (
    <Stack gap="sm">
      <TextInput
        label="Naziv"
        placeholder="Hramba kompleta gum – sezona"
        withAsterisk
        value={p.naziv ?? ''}
        onChange={(e) => {
          const v = e.currentTarget.value;
          setP((x) => ({ ...x, naziv: v }));
        }}
        error={polja.naziv}
      />
      <SimpleGrid cols={{ base: 1, xs: 3 }} spacing="sm">
        <TextInput
          label="Enota"
          value={p.enota ?? ''}
          onChange={(e) => {
            const v = e.currentTarget.value;
            setP((x) => ({ ...x, enota: v }));
          }}
          error={polja.enota}
        />
        <NumberInput
          label="Cena brez DDV"
          value={p.cena ?? ''}
          onChange={(v) => setP((x) => ({ ...x, cena: typeof v === 'number' ? v : undefined }))}
          error={polja.cena}
          thousandSeparator="."
          rightSection="€"
          {...decimalno}
        />
        <NumberInput
          label="DDV"
          description="Prazno = podjetja"
          value={p.ddvStopnja ?? ''}
          onChange={(v) => setP((x) => ({ ...x, ddvStopnja: typeof v === 'number' ? v : undefined }))}
          error={polja.ddvStopnja}
          max={100}
          rightSection="%"
          {...decimalno}
        />
      </SimpleGrid>
      {shrani.error && !(shrani.error instanceof NapakaApi && Object.keys(shrani.error.polja).length > 0) && (
        <Alert color="red" variant="light" role="alert">
          {sporocilo(shrani.error)}
        </Alert>
      )}
      <Group justify="flex-end" gap="xs">
        <Button variant="subtle" onClick={zapri} disabled={shrani.isPending}>
          Prekliči
        </Button>
        <Button onClick={() => shrani.mutate()} loading={shrani.isPending}>
          Shrani
        </Button>
      </Group>
    </Stack>
  );
}
